import type { DotLottie } from '@lottiefiles/dotlottie-react';
import { isLinkActive } from '@sohanemon/utils';
import { SohanScriptLoader } from '@sohanscript/ui/sohanscript-loader';
import { useLocation } from '@tanstack/react-router';
import {
	lazy,
	memo,
	Suspense,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { useDraft } from '#/lib/hooks/use-draft.tsx';
import { getBrand } from '#/lib/utils/app-env.ts';

const SetupLottie = lazy(() =>
	import('@sohanscript/ui/animated/setup-lottie').then((module) => ({
		default: module.SetupLottie,
	})),
);

type PreloaderProps = { duration?: number };

export const Preloader = memo(function Preloader({
	duration = 5000,
}: PreloaderProps) {
	const location = useLocation();
	const currentPath = location.pathname;
	const home = isLinkActive({ targetPath: '/', currentPath });

	const ref = useRef<HTMLDivElement>(null);
	const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);
	const [done, setDone] = useState(false);
	const [lottieLoaded, setLottieLoaded] = useState(false);
	// NOTE: the simple non-home loader needs less time than the home cinematic.
	const activeDuration = home ? duration : Math.min(duration, 1500);

	const { isInIframe } = useDraft();

	const onLottieLoad = useCallback(() => setLottieLoaded(true), []);
	const dotLottieRefCallback = useCallback((instance: DotLottie | null) => {
		setDotLottie(instance);
	}, []);

	useEffect(() => {
		if (!dotLottie) return;

		dotLottie.addEventListener('load', onLottieLoad);
		return () => dotLottie.removeEventListener('load', onLottieLoad);
	}, [dotLottie, onLottieLoad]);

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;

		if (document.documentElement.classList.contains('preloader-done')) {
			setDone(true);
			return;
		}

		const dismiss = () => {
			el.classList.add('preloader-exiting');
		};

		const exitTimer = setTimeout(dismiss, activeDuration);
		const interactionEvents = [
			'pointerdown',
			'wheel',
			'touchstart',
			'keydown',
		] as const;

		for (const event of interactionEvents) {
			window.addEventListener(event, dismiss, { passive: true });
		}

		const onEnd = (e: AnimationEvent) => {
			if (e.animationName === 'preloaderExit') {
				document.documentElement.classList.add('preloader-done');
				setDone(true);
			}
		};

		el.addEventListener('animationend', onEnd);
		return () => {
			clearTimeout(exitTimer);
			for (const event of interactionEvents) {
				window.removeEventListener(event, dismiss);
			}
			el.removeEventListener('animationend', onEnd);
		};
	}, [activeDuration]);

	if (done || isInIframe) return null;

	return (
		<div
			ref={ref}
			id="preloader"
			className="fixed inset-0 z-9999 flex items-center justify-center bg-background"
			style={
				{
					'--preloader-exit-duration': `${0.0001 * activeDuration}s`,
				} as React.CSSProperties
			}
		>
			{/* Vignette overlay */}
			<div
				className="pointer-events-none absolute inset-0"
				style={{
					background:
						'radial-gradient(ellipse at center, transparent 40%, var(--preloader-vignette) 100%)',
				}}
			/>
			<div className="relative flex items-center justify-center">
				{home ? (
					<>
						{!lottieLoaded && (
							<center className="h-54 absolute grid place-content-center">
								<SohanScriptLoader className="size-26" />
							</center>
						)}
						<Suspense fallback={null}>
							<SetupLottie
								className="w-screen lg:w-[50vw]"
								dotLottieRefCallback={dotLottieRefCallback}
							/>
						</Suspense>
						{lottieLoaded && (
							<div className="reveal-after-3500 absolute inset-x-0 top-4/14 mx-auto ps-2 text-center font-bold font-matrix text-[clamp(11px,1.5vw,20px)] text-primary">
								{getBrand().domain}
							</div>
						)}
					</>
				) : (
					<center className="h-54 grid place-content-center">
						<SohanScriptLoader className="size-26" />
					</center>
				)}
			</div>
			<style>{`
body,html {overflow:clip;}
#top-loader { display: none;}

body:has(#preloader) *:not(:has(#preloader)):not(#preloader):not(#preloader *) {
  visibility: hidden;
}
@keyframes preloaderExit {
	from {
    opacity: 1;
	}
	to {
    opacity: 0;
	}
}

.preloader-exiting {
	animation: preloaderExit var(--preloader-exit-duration, 0.35s) ease-in-out forwards;
}
`}</style>
		</div>
	);
});
